/**
 * The data layer: thin wrappers over TanStack Query so a page states what it
 * needs rather than restating the envelope, the key, and the error mapping.
 *
 * Two rules from the spec show up here rather than in the pages:
 *
 *  - **Free-text search is `POST` with the term in the body** (§9). There is
 *    no GET search helper, deliberately: the shareable URL may carry the term
 *    for client state, the request must not.
 *  - Every mutation may return `patternWarnings` (§8 item 4) and a
 *    `concurrencyNotice` (§6.10). `useApiMutation` surfaces both as toasts by
 *    default, so a page cannot forget one.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { MAX_PAGE_SIZE, type ConcurrencyNotice, type PatternWarning } from '@menlo/shared';
import { api, ApiError } from '@/lib/api';
import { useToast } from '@/lib/toast';
import { humanize } from '@/lib/format';

export interface Paged<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  /** Reported by the opportunity list: the count had to evaluate the view. */
  countedOverView?: boolean;
}

export type Row = Record<string, unknown>;

/** A GET, unwrapped, with 4xx treated as an answer rather than a failure. */
export function useApiQuery<T>(
  key: readonly unknown[],
  path: string | null,
  options: Omit<UseQueryOptions<T, ApiError>, 'queryKey' | 'queryFn'> = {},
): UseQueryResult<T, ApiError> {
  return useQuery<T, ApiError>({
    queryKey: key,
    queryFn: () => api<T>(path as string),
    enabled: path !== null && (options.enabled ?? true),
    retry: (failureCount, error) =>
      error instanceof ApiError && error.status >= 400 && error.status < 500
        ? false
        : failureCount < 2,
    ...options,
  });
}

/**
 * Every row of a paged list, for a screen built on the WHOLE set — one that
 * searches, filters or nests on the client. It asks for pages of
 * `MAX_PAGE_SIZE` until `total` is reached, so the result is never one page
 * that silently stops at the cap (docs/bugs.md session 6: dev passed 200
 * accounts and the Accounts page dropped the rest without a word).
 *
 * `path` carries the filters and sort, without `page` or `pageSize`. `items`
 * and `total` come back as one `Paged`, so a caller reads it like any page.
 */
export function useApiQueryAllPages<T>(
  key: readonly unknown[],
  path: string | null,
): UseQueryResult<Paged<T>, ApiError> {
  return useQuery<Paged<T>, ApiError>({
    queryKey: [...key, 'all-pages'],
    enabled: path !== null,
    retry: (failureCount, error) =>
      error instanceof ApiError && error.status >= 400 && error.status < 500
        ? false
        : failureCount < 2,
    queryFn: async () => {
      const separator = (path as string).includes('?') ? '&' : '?';
      const items: T[] = [];
      let total = 0;
      // Bounded: a total that keeps moving cannot loop forever.
      for (let page = 1; page <= 100; page += 1) {
        const result = await api<Paged<T>>(
          `${path as string}${separator}page=${page}&pageSize=${MAX_PAGE_SIZE}`,
        );
        items.push(...result.items);
        total = result.total;
        if (result.items.length === 0 || items.length >= total) break;
      }
      return { items, total, page: 1, pageSize: items.length };
    },
  });
}

/** A POST whose body carries a search term or a filter set (§9). */
export function useApiPost<T>(
  key: readonly unknown[],
  path: string,
  body: unknown,
  options: Omit<UseQueryOptions<T, ApiError>, 'queryKey' | 'queryFn'> = {},
): UseQueryResult<T, ApiError> {
  return useQuery<T, ApiError>({
    queryKey: key,
    queryFn: () => api<T>(path, { method: 'POST', body }),
    retry: (failureCount, error) =>
      error instanceof ApiError && error.status >= 400 && error.status < 500
        ? false
        : failureCount < 2,
    ...options,
  });
}

export interface MutationEnvelope {
  patternWarnings?: PatternWarning[];
  concurrencyNotice?: ConcurrencyNotice | null;
  [key: string]: unknown;
}

export interface ApiMutationOptions<TVariables, TData> {
  /** Query keys to invalidate on success. */
  invalidate?: readonly (readonly unknown[])[];
  onSuccess?: (data: TData, variables: TVariables) => void;
  onError?: (error: ApiError, variables: TVariables) => void;
  /** Suppress the automatic error toast — a form that renders the error itself. */
  quiet?: boolean;
}

/**
 * A mutation that reports what the server reported.
 *
 * The concurrency notice is a toast with a `Reload` action and never a dialog:
 * §6.10 is explicit that there is no `version` column, no 409, and no conflict
 * dialog. The pattern warning is a toast that stays until dismissed, naming
 * the field and the class — **never the matched text**, which the server does
 * not send and this code could not print if it wanted to.
 */
export function useApiMutation<TData extends MutationEnvelope, TVariables = void>(
  request: (variables: TVariables) => {
    path: string;
    method: 'POST' | 'PUT' | 'DELETE';
    body?: unknown;
  },
  options: ApiMutationOptions<TVariables, TData> = {},
): UseMutationResult<TData, ApiError, TVariables> {
  const queryClient = useQueryClient();
  const { push } = useToast();

  return useMutation<TData, ApiError, TVariables>({
    mutationFn: async (variables) => {
      const { path, method, body } = request(variables);
      return api<TData>(path, { method, ...(body === undefined ? {} : { body }) });
    },
    onSuccess: (data, variables) => {
      for (const warning of data?.patternWarnings ?? []) {
        push({
          tone: 'warning',
          title: `Possible export-controlled content in "${warning.field}"`,
          body:
            `Pattern class: ${humanize(warning.patternClass)}. Saved anyway — this is a warning, ` +
            'not a block. This system is not authorized to hold export-controlled technical data.',
        });
      }

      const notice = data?.concurrencyNotice;
      if (notice) {
        push({
          tone: 'info',
          title: `${notice.changedBy} also changed this`,
          body: `${notice.fields.map(humanize).join(', ')} — last write wins, so yours is the one saved.`,
          action: {
            label: 'Reload',
            onClick: () => void queryClient.invalidateQueries(),
          },
        });
      }

      for (const key of options.invalidate ?? []) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
      options.onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      if (!options.quiet) {
        push({ tone: 'error', title: error.message, body: `Code: ${error.code}` });
      }
      options.onError?.(error, variables);
    },
  });
}
